<template>
    <div class="group">
        <codemirror
            ref="codemirrorInstance"
            v-model="modelValue"
            :style="{ minHeight: '200px' }"
            :autofocus="true"
            :indent-with-tab="true"
            :tab-size="2"
            :extensions="extensions"
            @drop.prevent="handleDrop"
            @dragover.prevent
        />
    </div>
</template>

<script setup lang="ts">
import { ref, watchEffect } from 'vue';
import type { FieldComponent } from '@src/Common/Components/types';
import { Codemirror } from 'vue-codemirror';
import { EditorView } from '@codemirror/view';
import { html } from '@codemirror/lang-html';
import { githubLight } from '@src/Common/CodemirrorThemes/GithubLight';
import { githubDark } from '@src/Common/CodemirrorThemes/GithubDark';
import { useColorMode } from '@vueuse/core';

const codemirrorInstance = ref(null);
const extensions = ref([]);

const mode = useColorMode({
    emitAuto: true,
});

const detectSystemColorScheme = () => {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    return prefersDark ? 'dark' : 'light';
};

const updateExtensions = () => {
    const currentMode = mode.value === 'auto' ? detectSystemColorScheme() : mode.value;
    extensions.value = [html(), currentMode === 'light' ? githubLight : githubDark, EditorView.lineWrapping];
};

const modelValue = defineModel({
    type: String,
});

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const props = withDefaults(defineProps<FieldComponent>(), {});

watchEffect(() => {
    updateExtensions();

    if (codemirrorInstance.value?.editor) {
        codemirrorInstance.value.editor.dispatch({
            effects: EditorView.reconfigure.of(extensions.value),
        });
    }
});

const handleDrop = (event: DragEvent) => {
    const text = event.dataTransfer?.getData('text/plain');
    if (!text || !codemirrorInstance.value) return;

    const editor = codemirrorInstance.value.editor;

    if (!editor) return;

    const pos = editor.posAtCoords({ x: event.clientX, y: event.clientY });

    if (pos !== null) {
        editor.dispatch({
            changes: {
                from: pos,
                to: pos,
                insert: text,
            },
        });
    }
};
</script>
